import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Prompt, useHistory, useParams } from 'react-router-dom';
import { Loading } from 'src/components/Loading';
import { SCRoutes } from 'src/config';
import { useDashboardFetchAgency, useSubmitAgencyEdit } from '../hooks';
import { EditAgencyForm } from './agencyEdit/components/EditAgencyForm';

type Props = {
  testID?: string;
};

export const DashboardAgenciesEdit: React.FC<Props> = ({ testID = 'DashboardAgenciesEdit' }) => {
  const { push } = useHistory();
  const { t } = useTranslation();
  const [isDirty, setIsDirty] = useState(true);
  const { id } = useParams<{ id: string }>();

  const { fetchAgency, isLoading, data: agency, error, isConnected } = useDashboardFetchAgency();
  const {
    isLoading: isSubmitting,
    agencyUpdated,
    error: errorEdit,
    submitEdit,
  } = useSubmitAgencyEdit({ agencyId: agency?.id });

  useEffect(() => {
    if (id) {
      fetchAgency(id);
    }
  }, [fetchAgency, id]);

  useEffect(() => {
    if (agencyUpdated) {
      setIsDirty(false);
      push(SCRoutes.dashboard.agencies.home);
    }
  }, [agencyUpdated, fetchAgency, id, push, t]);

  if (isLoading || isSubmitting) {
    return <Loading />;
  }

  if (error) {
    return <div className="flex justify-center content-center min-h-screen">{error.message}</div>;
  }

  if (!agency) {
    return (
      <div className="flex justify-center content-center min-h-screen">
        {t('dashboard-agency-edit.agency-not-found')}
      </div>
    );
  }

  return (
    <>
      <Prompt when={isDirty} message={t('dashboard-agencies-edit.confirm-exit-without-update')} />
      <div className="bg-white min-h-screen pt-3">
        <div data-testid={testID} className="mx-4">
          <div
            className="text-xl text-gray-600 inline-block"
            dangerouslySetInnerHTML={{
              __html: t('dashboard-agencies-edit.agency-edit-page-title', { name: agency.name }),
            }}
          />
          {isConnected && <span className="bg-green-500 mr-2 h-3 w-3 rounded-full inline-block ml-2"></span>}
        </div>

        <div>
          <EditAgencyForm
            initialValues={agency}
            onTouched={setIsDirty}
            errorEdit={errorEdit}
            submitEdit={submitEdit}
            isParent={agency.isPartner}
          />
        </div>
      </div>
    </>
  );
};
